



































































import { Component, Mixins, Prop, Watch, Emit } from "vue-property-decorator";
import { PdfjsAccessor } from "./PdfjsAccessor";
import PdfjsViewerContextMenu from "./PdfjsViewerContextMenu.vue";
import { ComponentWatchableMixin } from "../mixins";
import { NumberSelector } from "../selectors";
import { StandardOption } from "../selectors/mixins";
import OptionDropDownSelector from "../selectors/OptionDropDownSelector.vue";
import { PdfViewType } from "../../../domain/configuration";

@Component({
  components: {
    NumberSelector,
    selector: OptionDropDownSelector,
    PdfjsViewerContextMenu,
  },
})
export default class PdfjsViewer extends Mixins(ComponentWatchableMixin) {
  @Prop()
  src: string;

  @Prop({ default: 1 })
  page: number;

  @Prop({ default: "Fit" })
  view: "" | "Fit" | "FitH" | "FitV";

  @Prop({ default: 0 })
  zoom: number;

  @Prop({ default: false })
  extension: boolean;

  @Prop()
  forceEnableOpenTypeFontFace: boolean;

  $refs!: {
    pdf: HTMLDivElement,
    contextMenu: PdfjsViewerContextMenu,
  };

  data() {
    return {
      enabledTextLayer: false,
      coloredTextLayer: false,
      autoColoredTextLayer: false,
    };
  }

  currentPage: number = 0;
  numPages: number = 0;
  private currentScale: number = 1;
  private selectedView: PdfViewType = "Fit";
  private readonly fixedViewScales: number[] = [0.5, 0.75, 1.0, 1.25, 1.5, 2.0];
  private readonly scaleRange: number = 0.1;

  private mountedIntervalId: number = null;
  private pdf: PdfjsAccessor = null;
  private isReady: boolean = false;
  private isLoad: boolean = false;

  private enabledTextLayer: boolean;
  private coloredTextLayer: boolean;
  private autoColoredTextLayer: boolean;

  get isLoading(): boolean {
    return this.isReady && !this.isLoad;
  }

  get viewOptions(): StandardOption<PdfViewType>[] {
    const scale = this.toViewOptionScale(this.selectedView);
    return this.fixedViewScales.map((v) => new StandardOption<PdfViewType>(Math.round(v * 100) + "%", v))
      .filter((option) => this.toViewOptionScale(option.value) !== scale)
      .concat(new StandardOption<PdfViewType>(Math.round(this.toViewOptionScale(this.selectedView) * 100) + "%", this.selectedView))
      .sort((prev, next) => this.toViewOptionScale(prev.value) - this.toViewOptionScale(next.value));
  }

  get accessorOption(): {
    selectableTextLayer: boolean,
    isPDFSinglePageViewer: boolean,
    useOnlyCssZoom: boolean,
    disableFontFace: boolean } {
    return this.$configuration.system.pdfRendering.accessorOption;
  }

  get enabledTextLayerClass(): string {
    return this.enabledTextLayer
      ? "selected"
      : "";
  }

  get coloredTextLayerClass(): string {
    return this.coloredTextLayer || this.autoColoredTextLayer
      ? "selected"
      : "";
  }

  get switchPdfViewer(): boolean {
    return this.$configuration.system.pdfRendering.switchPdfViewer;
  }

  get pdfViewerClass(): string {
    return [
      this.isTextLayerColored ? "colored-text-layer" : null,
      this.isTextLayerEnabled ? null : "invisible-text-layer",
    ].filter((v) => v).join(" ");
  }

  get isTextLayerColored(): boolean {
    // selectableTextLayerが有効の場合はtextLayerの有無で挙動切り替わるので常に色付けした状態で問題無い
    return this.accessorOption.selectableTextLayer || this.coloredTextLayer || this.autoColoredTextLayer;
  }

  get isTextLayerEnabled(): boolean {
    // 何も手を加えない状態だとtextLayerは透過（テキスト選択は可能）な状態
    // selectableTextLayerが有効の場合はenabledTextLayerしだいでtextLayerの有無を切り替える
    return !this.accessorOption.selectableTextLayer || this.enabledTextLayer;
  }

  mounted(): void {
    const pdfRenderingViewType = this.$configuration.system.pdfRendering.viewType;
    this.selectedView = pdfRenderingViewType
      ? typeof pdfRenderingViewType !== "number"
        ? pdfRenderingViewType
        : pdfRenderingViewType / 100
      : this.view !== ""
        ? this.view
        : this.zoom;
    // $refs関連はブラウザリロードするとmounted()タイミングでは取得失敗する。
    // nextTick()ではPdfjsAccessorのconstructorに渡った際、containerがundefinedになる場合があった。
    // IEでのasyncの展開が悪いのか、asyncのメソッドで$refsのチェックをして渡しても同様になった。setInterval()で対応する。
    this.mountedIntervalId = setInterval(() => {
      if (!this.$refs.pdf) {
        return;
      }
      clearInterval(this.mountedIntervalId);
      this.watchHtmlElementResized("div", (n, o) => this.onResized());
      this.pdf = new PdfjsAccessor(
        this.$refs.pdf as HTMLDivElement,
        this.accessorOption.isPDFSinglePageViewer,
        this.accessorOption.useOnlyCssZoom,
        this.forceEnableOpenTypeFontFace ? false : this.accessorOption.disableFontFace);
      this.loadDocument();
    });
  }

  beforeDestroy(): void {
    if (this.mountedIntervalId !== null) {
      clearInterval(this.mountedIntervalId);
    }
    this.pdf && this.pdf.cleanupDocument();
  }

  @Watch("src")
  onDocumentChanged(): void {
    if (!this.pdf) {
      return;
    }
    this.loadDocument();
  }

  @Watch("page")
  onPageChanged(): void {
    this.currentPage = this.page;
  }

  @Watch("currentPage")
  onCurrentPageChanged(): void {
    if (!this.isLoad) {
      return;
    }
    this.pdf.currentPageNumber = this.currentPage;
  }

  @Watch("selectedView")
  onSelectedViewChanged(): void {
    if (!this.isLoad) {
      return;
    }
    this.updateScale();
  }

  forwardPage(): void {
    if (this.currentPage < this.numPages) {
      this.currentPage++;
    }
  }

  backPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  scaleUp(): void {
    const nextView = Math.round((this.pdf.currentScale + this.scaleRange) * 100) / 100;
    if (nextView <= Math.max(...this.fixedViewScales)) {
      this.selectedView = nextView;
    }
  }

  scaleDown(): void {
    const nextView = Math.round((this.pdf.currentScale - this.scaleRange) * 100) / 100;
    if (nextView >= Math.min(...this.fixedViewScales)) {
      this.selectedView = nextView;
    }
  }

  toSelectedClass(value: PdfViewType): string {
    return this.selectedView === value
      ? "selected"
      : "";
  }

  onScroll(): void {
    this.currentPage = this.pdf.currentPageNumber;
  }

  private toViewOptionScale(value: PdfViewType): number {
    const scale = typeof value === "number"
      ? value
      : this.currentScale;
    return Math.round(scale * 100) / 100;
  }

  private async loadDocument(): Promise<void> {
    if (this.src) {
      this.isReady = true;
      this.isLoad = false;
      this.currentPage = 1;
      this.numPages = 1;
      if (!await this.pdf.loadDocument(this.src)) {
        return;
      }
      this.isLoad = true;
      this.numPages = this.pdf.numPages;
      if (this.page && this.page <= this.numPages) {
        this.$nextTick(() => {
          // IEはnextTickしないと反映しない模様
          this.onPageChanged();
        });
      }
      this.updateScale();
    } else {
      this.isReady = false;
      this.isLoad = false;
      this.pdf.cleanupDocument();
    }
  }

  private updateScale(): void {
    if (typeof this.selectedView === "number") {
      this.pdf.currentScale = this.selectedView;
    } else {
      this.setCurrentScaleValue(this.selectedView);
    }
  }

  private onResized(): void {
    if (typeof this.selectedView === "number") {
      return;
    }
    this.setCurrentScaleValue(this.selectedView);
  }

  private setCurrentScaleValue(value: "" | "Fit" | "FitH" | "FitV"): void {
    switch (value) {
      case "FitV":
        this.pdf.currentScaleValue = "page-height";
        break;
      case "FitH":
        this.pdf.currentScaleValue = "page-width";
        break;
      case "Fit":
        this.pdf.currentScaleValue = "page-fit";
        break;
      default:
        this.pdf.currentScaleValue = "page-actual";
        break;
    }
    this.selectedView = value;
    this.currentScale = this.pdf.currentScale;
  }

  @Emit()
  changePdfViewer(): void {}

  onColoredTextLayerButtonClicked(): void {
    if (this.autoColoredTextLayer) {
      this.autoColoredTextLayer = false;
      this.coloredTextLayer = false; // 自動色付けした場合はtrueになっているケースは無いはずだが念のため
      return;
    }
    this.coloredTextLayer = !this.coloredTextLayer;
  }

  private _isMouseDown = false;
  private _isMovedWithMouseDown = false;

  onMouseDown(event: any): void {
    this._isMouseDown = true;
    this._isMovedWithMouseDown = false;
  }

  onMouseUp(event: any): void {
    //  自動で色付け選択された状態から再度選択した場合のちらつきを抑えるためにMouseUpで判定している
    if (!this._isMovedWithMouseDown) {
      this.autoColoredTextLayer = false;
    }
    this._isMouseDown = false;
    this._isMovedWithMouseDown = false;
  }

  onMouseLeave(event: any): void {
    this._isMouseDown = false;
    this._isMovedWithMouseDown = false;
  }

  onMouseMove(event: any): void {
    if (!this._isMouseDown) { return; }
    this._isMovedWithMouseDown = true;

    if (!this.accessorOption.selectableTextLayer && !this.coloredTextLayer) {
      this.autoColoredTextLayer = true;
    }
  }

  onRootPointerDowned(event: PointerEvent): void {
    this.$refs.contextMenu.handlePointerDowned(event);
  }
}
